/**
 * The **rendered sentence** of an automation, the way monday prints it (T-8d).
 *
 * monday never shows a recipe's slot labels on a card. Capture `15-after-create.json` reads
 *
 *   When **Status** changes to **Done**, create a pre-made update
 *
 * — the fixed words in the card's text colour and every **configured** value in bold, the same
 * text `renderTitle` stores on the server (`apps/api/src/modules/automations/service.ts` §8.1).
 * Ours printed the placeholders instead ("When a status changes to something…"), because the
 * centre assembled the sentence from `parseSentence` alone and dropped the node's params.
 *
 * The resolution of one slot is **not** duplicated here: `formatSlotValue` is the same function
 * the editor's `SlotChip` uses, so a chip and a card can never disagree about what a value
 * reads as. A slot nobody configured keeps monday's grey default label, which is what an
 * unfinished recipe looks like in the editor too.
 */
import {
  automationBlockRegistry,
  isSlotFilled,
  parseSentence,
  type AutomationRecipe,
} from '@elizmonday/shared';
import { formatSlotValue } from './slots';
import type { PickerDataView } from './types';

/** One run of the sentence. `value` is what monday draws bold; `placeholder` is its grey default. */
export type SentenceChunk =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'value'; readonly text: string }
  | { readonly kind: 'placeholder'; readonly text: string };

interface SentenceNode {
  readonly block: string;
  readonly params: Readonly<Record<string, unknown>>;
}

/**
 * Which column a node's label/dropdown slots belong to — a status label is an **index**, and
 * only its column knows the text. Shared with `RecipeEditor`, which needs the same context.
 */
export function columnOfNode(node: SentenceNode): string | null {
  for (const key of ['columnId', 'dropdownColumnId', 'dateColumnId']) {
    const value = node.params[key];
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      const id = (value as { columnId?: unknown }).columnId;
      if (typeof id === 'string') return id;
    }
  }
  return null;
}

/**
 * The recipe as chunks, in sentence order. `pickers` is what turns an id into a name; with no
 * picker data (a card rendered before the board's columns arrived) every filled slot still
 * renders — `formatSlotValue` falls back to the raw id rather than to the placeholder.
 */
export function sentenceChunks(recipe: AutomationRecipe, pickers: PickerDataView | null): SentenceChunk[] {
  const empty: PickerDataView = { columns: [], subitemColumns: [], groups: [], people: [], boards: [], me: null };
  const data = pickers ?? empty;
  const nodes = [recipe.trigger, ...recipe.conditions, ...recipe.actions];
  const chunks: SentenceChunk[] = [];

  nodes.forEach((node, index) => {
    if (index > 0) chunks.push({ kind: 'text', text: ' ' });
    const block = automationBlockRegistry.find(node.block);
    if (block === undefined) {
      chunks.push({ kind: 'text', text: node.block });
      return;
    }
    const columnId = columnOfNode(node);
    for (const part of parseSentence(block)) {
      if (part.kind === 'text') {
        chunks.push({ kind: 'text', text: part.text });
        continue;
      }
      const value = node.params[part.param];
      if (!isSlotFilled(value)) {
        chunks.push({ kind: 'placeholder', text: part.label });
        continue;
      }
      const text = formatSlotValue(part.slot.kind, value, data, { columnId });
      // A configured slot with no printable form (a message body: monday keeps the word
      // "notify") stays on its label — the same rule `SlotChip` applies.
      chunks.push(text === null ? { kind: 'placeholder', text: part.label } : { kind: 'value', text });
    }
  });

  return collapse(chunks);
}

/** Squeezes the whitespace the way monday's one-line card does, without losing the chunking. */
function collapse(chunks: readonly SentenceChunk[]): SentenceChunk[] {
  const out: SentenceChunk[] = [];
  for (const chunk of chunks) {
    const text = chunk.kind === 'text' ? chunk.text.replace(/\s+/g, ' ') : chunk.text.trim();
    if (text === '') continue;
    const previous = out.at(-1);
    if (previous !== undefined && previous.kind === 'text' && chunk.kind === 'text' && previous.text.endsWith(' ')) {
      out[out.length - 1] = { kind: 'text', text: previous.text + text.replace(/^\s+/, '') };
      continue;
    }
    out.push(chunk.kind === 'text' ? { kind: 'text', text } : { kind: chunk.kind, text });
  }
  const first = out[0];
  if (first !== undefined && first.kind === 'text') out[0] = { kind: 'text', text: first.text.replace(/^\s+/, '') };
  const last = out.at(-1);
  if (last !== undefined && last.kind === 'text') out[out.length - 1] = { kind: 'text', text: last.text.replace(/\s+$/, '') };
  return out.filter((chunk) => chunk.text !== '');
}

/** The same sentence in plain text — what the list searches and filters on. */
export function sentenceText(chunks: readonly SentenceChunk[]): string {
  return chunks
    .map((chunk, index) => {
      const previous = chunks[index - 1];
      const glue = previous !== undefined && previous.kind !== 'text' && chunk.kind !== 'text' ? ' ' : '';
      return glue + chunk.text;
    })
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

export function automationSentence(recipe: AutomationRecipe, pickers: PickerDataView | null): string {
  return sentenceText(sentenceChunks(recipe, pickers));
}

/** The card's own rendering: configured values bold, monday's grey defaults grey. */
export function AutomationSentence({
  chunks,
  className,
  testId,
}: {
  chunks: readonly SentenceChunk[];
  className?: string;
  testId?: string;
}) {
  return (
    <span className={className} data-testid={testId}>
      {chunks.map((chunk, index) =>
        chunk.kind === 'text' ? (
          <span key={`t${String(index)}`}>{chunk.text}</span>
        ) : chunk.kind === 'value' ? (
          <b key={`v${String(index)}`} className="ez-automation-sentence__value">
            {chunk.text}
          </b>
        ) : (
          <span key={`p${String(index)}`} className="ez-automation-sentence__placeholder">
            {chunk.text}
          </span>
        ),
      )}
    </span>
  );
}
